import ExcelJS from 'exceljs';
import {
  buildAnnualProgressLearningCrossSuffix,
  type AnnualCrossTopicDetailRow,
} from './annualProgressCrossTopicExtract';

/** `App.css` 테이블 열 비율(%) — 프로그램 내용 모달과 동일 */
const COLUMN_WIDTH_PERCENT = [3.5, 7, 3.5, 4, 7, 10, 27, 5.5, 5.5, 3.8, 3.8, 11] as const;

const HEADERS = [
  '순서',
  '날짜',
  '요일',
  '교시',
  '편제\n(시간표명)',
  '단원',
  '프로그램 내용',
  '쪽수',
  '보조쪽수',
  '해당차시',
  '전체차시',
  '준비물',
] as const;

const COLUMN_COUNT = HEADERS.length;
const EXCEL_BASE_TOTAL_WCH = 100;
const HEADER_ROW_INDEX = 2;
const DATA_START_ROW_INDEX = 3;
/** `rowToCells` 기준 0-based 열 인덱스 */
const CURRENT_PERIOD_COL_INDEX = 9;
const TOTAL_PERIOD_COL_INDEX = 10;
const PERIOD_NUMBER_FORMAT = '0';

function solidCellBorder(): Partial<ExcelJS.Borders> {
  const side: ExcelJS.Border = { style: 'thin', color: { argb: 'FF000000' } };
  return {
    top: { ...side },
    left: { ...side },
    bottom: { ...side },
    right: { ...side },
  };
}

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFF4F4F4' },
};

function displayWidthOfText(text: string): number {
  let width = 0;
  for (const ch of text) {
    width += ch.charCodeAt(0) > 255 ? 2 : 1;
  }
  return width;
}

function formatLearningCell(row: AnnualCrossTopicDetailRow): string {
  const suffix = buildAnnualProgressLearningCrossSuffix(row.crossTopic, row.crossNote);
  if (!suffix) return row.learning;
  return row.learning ? `${row.learning} ${suffix}` : suffix;
}

function rowToCells(row: AnnualCrossTopicDetailRow, displayIndex: number): string[] {
  return [
    String(displayIndex),
    row.dateLabel,
    row.weekdayLabel,
    row.periodLabel,
    row.templateLabel,
    row.unit,
    formatLearningCell(row),
    row.page,
    row.assistantPage,
    row.currentPeriod,
    row.totalPeriod,
    row.material,
  ];
}

function buildColumnWidths(dataRows: string[][]): number[] {
  const allRows = [HEADERS as unknown as string[], ...dataRows];
  const percentSum = COLUMN_WIDTH_PERCENT.reduce((sum, pct) => sum + pct, 0);

  return COLUMN_WIDTH_PERCENT.map((pct, colIndex) => {
    const cssWch = (pct / percentSum) * EXCEL_BASE_TOTAL_WCH;
    const contentWch = Math.max(
      ...allRows.map((row) => displayWidthOfText((row[colIndex] ?? '').trim())),
      2
    );
    return Math.min(72, Math.max(cssWch, contentWch + 1.5));
  });
}

function triggerBrowserDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function parsePeriodNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
}

function setDataCellValue(cell: ExcelJS.Cell, colOffset: number, value: string): void {
  if (colOffset === 0 || colOffset === CURRENT_PERIOD_COL_INDEX || colOffset === TOTAL_PERIOD_COL_INDEX) {
    const num = parsePeriodNumber(value);
    if (num !== null) {
      cell.value = num;
      cell.numFmt = PERIOD_NUMBER_FORMAT;
      return;
    }
  }
  cell.value = value;
}
function applyTableCellStyle(cell: ExcelJS.Cell, isHeader: boolean): void {
  cell.border = solidCellBorder();
  if (isHeader) {
    cell.font = { bold: true };
    cell.fill = HEADER_FILL;
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    return;
  }
  cell.alignment = { vertical: 'top', wrapText: true };
}

function styleTableRange(sheet: ExcelJS.Worksheet, lastRow: number): void {
  for (let rowIndex = HEADER_ROW_INDEX; rowIndex <= lastRow; rowIndex += 1) {
    for (let colIndex = 1; colIndex <= COLUMN_COUNT; colIndex += 1) {
      const cell = sheet.getCell(rowIndex, colIndex);
      const isHeader = rowIndex === HEADER_ROW_INDEX;
      cell.border = solidCellBorder();
      if (isHeader) {
        cell.font = { bold: true };
        cell.fill = HEADER_FILL;
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      } else {
        cell.alignment = { vertical: 'top', wrapText: true };
      }
    }
  }
}

export async function saveAnnualCrossTopicDetailAsExcel(params: {
  rows: AnnualCrossTopicDetailRow[];
  scopeLabel: string;
  topic: string;
  fileName: string;
  sheetName?: string;
}): Promise<void> {
  const { rows, scopeLabel, topic, fileName, sheetName = '범교과주제' } = params;
  if (rows.length === 0) return;

  const dataRows = rows.map((row, index) => rowToCells(row, index + 1));
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);

  const columnWidths = buildColumnWidths(dataRows);
  sheet.columns = columnWidths.map((width) => ({ width }));

  sheet.mergeCells(1, 1, 1, COLUMN_COUNT);
  const metaCell = sheet.getCell(1, 1);
  metaCell.value = `${scopeLabel} · ${topic}`;
  metaCell.font = { bold: true, size: 12 };
  metaCell.alignment = { horizontal: 'center', vertical: 'middle' };

  const headerRow = sheet.getRow(HEADER_ROW_INDEX);
  HEADERS.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    applyTableCellStyle(cell, true);
  });
  headerRow.height = 28;

  dataRows.forEach((cells, rowOffset) => {
    const row = sheet.getRow(DATA_START_ROW_INDEX + rowOffset);
    cells.forEach((value, colOffset) => {
      const cell = row.getCell(colOffset + 1);
      setDataCellValue(cell, colOffset, value);
    });
  });

  const lastRow = DATA_START_ROW_INDEX + dataRows.length - 1;
  styleTableRange(sheet, lastRow);

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBrowserDownload(buffer, fileName);
}
